import React from 'react';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import type { LinkedTradingAccountRow, PortfolioBalanceSummary, PortfolioCapitalAllocationDisplayRow, PortfolioLimitNotice } from './types';
import { formatCurrency } from './helpers';

interface CapitalDetailsModalProps {
    isOpen: boolean;
    linkedTradingAccount: LinkedTradingAccountRow;
    balanceSummary: PortfolioBalanceSummary;
    openPremiumReceived: number | null;
    unavailableCapitalBasisRows: PortfolioCapitalAllocationDisplayRow[];
    portfolioLimitNotices: PortfolioLimitNotice[];
    portfolioCapitalAllocationRows: PortfolioCapitalAllocationDisplayRow[];
    onManageAllocation: () => void;
    onClose: () => void;
}

const CapitalDetailsModal: React.FC<CapitalDetailsModalProps> = ({
    isOpen,
    linkedTradingAccount,
    balanceSummary,
    openPremiumReceived,
    unavailableCapitalBasisRows,
    portfolioLimitNotices,
    portfolioCapitalAllocationRows,
    onManageAllocation,
    onClose
}) => {
    return (
        <Modal isOpen={isOpen} size="xl" title="Brokerage Limits & Accounting Details" onClose={onClose}>
            <div className="p-3 text-white">
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h5 className="text-lg font-medium">Capital Allocation</h5>
                        <div className="mt-1 text-sm text-zinc-400">
                            Linked Account: {linkedTradingAccount.name}
                            {linkedTradingAccount.displayAccountId ? ` (${linkedTradingAccount.displayAccountId})` : ''}
                        </div>
                    </div>
                    <div className="max-w-xl text-sm text-zinc-400">
                        Non-cash allocations reserve broker-backed option capacity without changing portfolio cash.
                    </div>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[
                        {
                            label: 'Entire brokerage account value',
                            value: balanceSummary.brokerageEquity,
                            help: 'Includes every portfolio and holding on this account.'
                        },
                        {
                            label: 'Broker buying capacity',
                            value: balanceSummary.brokerExecutionCapacity,
                            help: 'Account-wide capacity for this portfolio’s trading type; not its remaining budget.'
                        },
                        {
                            label: 'Portfolio equity limit for new trades',
                            value: balanceSummary.currentEquity,
                            help: 'Broker-backed equity used when checking new entries.'
                        },
                        {
                            label: 'Budget supported by the broker',
                            value: balanceSummary.effectiveReportingCapitalBase,
                            help: 'Assigned budget after any brokerage shortfall.'
                        },
                        {
                            label: 'Net trading cash movement',
                            value: balanceSummary.cashLedgerBalance,
                            help: 'Trade payments and receipts after external funding is removed. This is not available cash.'
                        },
                        ...(openPremiumReceived === null
                            ? []
                            : [
                                  {
                                      label: 'Premium received on open short options',
                                      value: openPremiumReceived,
                                      help: 'Opening proceeds; profit depends on the cost of closing the options.'
                                  }
                              ])
                    ].map((item) => (
                        <div className="rounded-md border border-zinc-800 bg-[#121212] p-3" key={item.label}>
                            <div className="text-sm text-zinc-400">{item.label}</div>
                            <div className="mt-1 text-lg font-medium">
                                {item.value !== null && item.value < 0 ? '−' : ''}
                                {formatCurrency(item.value === null ? null : Math.abs(item.value))}
                            </div>
                            <div className="mt-1 text-xs text-zinc-500">{item.help}</div>
                        </div>
                    ))}
                </div>
                {balanceSummary.brokerageDataAsOf && (
                    <div className="mb-4 text-xs text-zinc-400">Brokerage balance updated {balanceSummary.brokerageDataAsOf} ET</div>
                )}

                {unavailableCapitalBasisRows.length > 0 && (
                    <div className="mb-4 rounded-md border border-yellow-700/60 bg-yellow-500/10 p-3 text-sm text-yellow-200">
                        <div className="font-medium mb-2">Unavailable Capital Bases</div>
                        <div className="space-y-1">
                            {unavailableCapitalBasisRows.map((summary) => (
                                <div key={`details-warning-${summary.capitalBasis}`}>
                                    {summary.label}: {summary.supportReason}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {portfolioLimitNotices.length > 0 && (
                    <div className="mb-4 rounded-md border border-blue-700/60 bg-blue-500/10 p-3 text-sm text-blue-100">
                        <div className="font-medium mb-2">Brokerage Limits & Notes</div>
                        <div className="space-y-2">
                            {portfolioLimitNotices.map((notice) => (
                                <div
                                    className={
                                        notice.tone === 'warning'
                                            ? 'rounded-md border border-yellow-700/50 bg-yellow-500/10 p-3 text-yellow-100'
                                            : 'rounded-md border border-blue-700/50 bg-blue-500/5 p-3 text-blue-100'
                                    }
                                    key={notice.id}
                                >
                                    <div className="font-medium mb-1">{notice.title}</div>
                                    <div>{notice.message}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-zinc-800 text-white">
                        <thead className="bg-[#121212]">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" scope="col">
                                    Capital Basis
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" scope="col">
                                    Portfolio Allocation
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" scope="col">
                                    Account Capacity
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" scope="col">
                                    Account Free Capacity
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" scope="col">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-[#222222] divide-y divide-zinc-800">
                            {portfolioCapitalAllocationRows.map((summary) => (
                                <tr className="hover:bg-zinc-800" key={`details-${summary.capitalBasis}`}>
                                    <td className="px-4 py-3 whitespace-nowrap">{summary.label}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(summary.portfolioAllocatedAmount)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(summary.totalCapacity)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(summary.availableToAllocate)}</td>
                                    <td className="px-4 py-3">
                                        {summary.overAllocatedAmount > 0 ? (
                                            <span className="text-yellow-300">
                                                Over capacity by {formatCurrency(summary.overAllocatedAmount)}
                                            </span>
                                        ) : summary.isSupported ? (
                                            <span className="text-zinc-300">Supported</span>
                                        ) : (
                                            <span className="text-yellow-400">{summary.supportReason ?? 'Unsupported'}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={onManageAllocation}>
                        Manage Allocation
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CapitalDetailsModal;
